import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  User,
  Package,
  Heart,
  Bell,
  Star,
  TicketPercent,
  LogOut,
  ChevronDown,
  ChevronUp,
  Camera,
  Loader2,
} from "lucide-react";
import WishlistPage from "../../common/WishlistPage";
import SelectAddressModal from "../Order/SelectAddressModal";
import UserOrders from "../../../screens/users/UserOrders";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { uploadToCloudinary } from "../../../utils/cloudinary";

const ProfilePage = () => {
  const [activeSection, setActiveSection] = useState("profile");
  const [accountOpen, setAccountOpen] = useState(true); // dropdown open by default
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const stored = JSON.parse(localStorage.getItem("pm-user-data"));
  const { t } = useTranslation();

  const fetchUser = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/customers/get-user-by-id/${stored._id}`
      );
      setUser(res.data.data);
      setFormData(res.data.data);
    } catch (err) {
      console.log("Fetch error:", err);
    }
  };

  useEffect(() => {
    if (stored?._id) fetchUser();
  }, []);

  if (!user) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-500">
        {t("profile.loading") || "Loading profile..."}
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ── Profile photo upload via Cloudinary 
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPG, PNG or WEBP images are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB.");
      return;
    }

    setImageUploading(true);
    try {
      const uploadRes = await uploadToCloudinary(file);
      const newImageUrl = uploadRes.secure_url;

      // Immediately save to backend
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/customers/update-by-user-id/${user._id}`,
        { ...formData, imageUrl: newImageUrl }
      );

      localStorage.setItem("pm-user-data", JSON.stringify(res.data.data));
      setUser(res.data.data);
      setFormData(res.data.data);
      toast.success("Profile photo updated!");
    } catch (err) {
      toast.error("Failed to upload image. Try again.");
    } finally {
      setImageUploading(false);
    }
  };

  // ── Save profile changes ─────────────────────────────────────────
  const updateProfile = async () => {
    setSaving(true);
    const loadingToast = toast.loading(
      t("profile.updating_profile") || "Updating profile..."
    );
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/customers/update-by-user-id/${user._id}`,
        formData
      );
      toast.dismiss(loadingToast);
      toast.success(t("profile.update_success") || "Profile updated successfully!");
      localStorage.setItem("pm-user-data", JSON.stringify(res.data.data));
      setEditMode(false);
      fetchUser();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(t("profile.update_failed") || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("pm-user-data");
    window.location.href = "/";
  };

  const menuItems = [
    { id: "orders", label: t("profile.menu_orders") || "MY ORDERS", icon: Package },
    { id: "wishlist", label: t("profile.menu_wishlist") || "MY WISHLIST", icon: Heart },
    {
      id: "account",
      label: t("profile.menu_account") || "ACCOUNT SETTINGS",
      icon: User,
      submenu: [
        { id: "profile", label: t("profile.menu_profile") || "Profile Information" },
        { id: "addresses", label: t("profile.menu_addresses") || "Manage Addresses" },
      ],
    },
    { id: "reviews", label: t("profile.menu_reviews") || "MY REVIEWS & RATINGS", icon: Star },
    { id: "notifications", label: t("profile.menu_notifications") || "NOTIFICATIONS", icon: Bell },
    { id: "coupons", label: t("profile.menu_coupons") || "MY COUPONS", icon: TicketPercent },
    { id: "logout", label: t("profile.menu_logout") || "LOGOUT", icon: LogOut },
  ];

  // Input style helper — keeps original look, highlights in edit mode
  const inputCls = (extra = "") =>
    `border p-3 rounded w-full transition-colors ${
      editMode
        ? "border-blue-400 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        : "border-gray-200 bg-gray-50 text-gray-700 cursor-default"
    } ${extra}`;

  return (
    <div className="py-10 bg-[#FBFBFB]">
      <div className="w-full mx-auto px-4 md:px-10 flex flex-col lg:flex-row gap-6">
        {/* ── LEFT SIDEBAR ─────────────────────────────────────── */}
        <div className="w-full lg:w-72">
          {/* Avatar card */}
          <div className="bg-white p-6 shadow rounded mb-4 flex items-center gap-3">
            {/* Clickable avatar with camera overlay */}
            <div className="relative flex-shrink-0">
              <img
                src={user.imageUrl || "https://ui-avatars.com/api/?name=" + user.firstName}
                className="w-14 h-14 rounded-full object-cover"
                alt="profile"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={imageUploading}
                title="Change photo"
                className="absolute -bottom-1 -right-1 bg-[#18385a] text-white rounded-full p-1 hover:bg-[#0f2740] transition-colors disabled:opacity-60"
              >
                {imageUploading ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Camera size={11} />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </div>

            <div>
              <p className="text-gray-500 text-sm">
                {t("profile.hello") || "Hello,"}
              </p>
              <p className="font-semibold font-philosopher text-xl">
                {user.firstName} {user.lastName}
              </p>
            </div>
          </div>

          {/* Menu */}
          <div className="bg-white shadow rounded">
            {menuItems.map((item) => (
              <div key={item.id}>
                <button
                  className="w-full flex justify-between items-center px-6 py-4 hover:bg-blue-50 border-b"
                  onClick={() => {
                    if (item.id === "logout") {
                      logout();
                    } else if (item.id === "account") {
                      // Toggle dropdown; also navigate to profile if closing
                      setAccountOpen((prev) => !prev);
                      if (!accountOpen) setActiveSection("profile");
                    } else if (!item.submenu) {
                      setActiveSection(item.id);
                      setAccountOpen(false);
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="text-blue-600" size={18} />
                    <span>{item.label}</span>
                  </div>
                  {item.submenu && (
                    accountOpen
                      ? <ChevronUp size={16} className="text-gray-400" />
                      : <ChevronDown size={16} className="text-gray-400" />
                  )}
                </button>

                {/* Submenu dropdown — only for account */}
                {item.submenu && accountOpen && (
                  <div className="bg-gray-50">
                    {item.submenu.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setActiveSection(s.id)}
                        className={`w-full px-10 py-3 text-left border-b hover:bg-blue-50 text-sm ${
                          activeSection === s.id
                            ? "text-blue-600 font-semibold"
                            : "text-gray-600"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT CONTENT ─────────────────────────────────────── */}
        <div className="flex-1 bg-white shadow rounded p-4 w-full">

          {/* PROFILE INFO */}
          {activeSection === "profile" && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold font-philosopher">
                  {t("profile.profile_info") || "Profile Information"}
                </h2>
                {!editMode && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="text-blue-600 text-sm hover:underline"
                  >
                    {t("profile.edit") || "Edit"}
                  </button>
                )}
              </div>

              {/* Profile photo section (bigger, inside right panel) */}
              <div className="flex items-center gap-5 mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="relative">
                  <img
                    src={user.imageUrl || "https://ui-avatars.com/api/?name=" + user.firstName + "&size=80"}
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                    alt="profile"
                  />
                  {imageUploading && (
                    <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                      <Loader2 size={20} className="text-white animate-spin" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-800">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-sm text-gray-500 mb-2">{user.phone}</p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={imageUploading}
                    className="text-xs px-3 py-1.5 border border-[#18385a] text-[#18385a] rounded hover:bg-[#18385a] hover:text-white transition-colors disabled:opacity-50 font-medium"
                  >
                    {imageUploading ? "Uploading..." : "Change Photo"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block uppercase tracking-wide">
                    {t("profile.first_name") || "First Name"}
                  </label>
                  <input
                    name="firstName"
                    value={formData.firstName || ""}
                    readOnly={!editMode}
                    onChange={handleChange}
                    className={inputCls()}
                    placeholder={t("profile.first_name") || "First Name"}
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block uppercase tracking-wide">
                    {t("profile.last_name") || "Last Name"}
                  </label>
                  <input
                    name="lastName"
                    value={formData.lastName || ""}
                    readOnly={!editMode}
                    onChange={handleChange}
                    className={inputCls()}
                    placeholder={t("profile.last_name") || "Last Name"}
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-xs text-gray-500 font-medium mb-1 block uppercase tracking-wide">
                    {t("profile.email") || "Email"}
                    <span className="normal-case font-normal text-gray-400 ml-1">(optional)</span>
                  </label>
                  <input
                    name="email"
                    value={formData.email || ""}
                    readOnly={!editMode}
                    onChange={handleChange}
                    className={inputCls()}
                    placeholder={t("profile.email") || "Email"}
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-xs text-gray-500 font-medium mb-1 block uppercase tracking-wide">
                    {t("profile.phone") || "Phone"}
                  </label>
                  <input
                    name="phone"
                    value={formData.phone || ""}
                    readOnly // phone always read-only (login identifier)
                    className="border p-3 rounded w-full border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed"
                    placeholder={t("profile.phone") || "Phone"}
                  />
                  <p className="text-xs text-gray-400 mt-1">Phone number cannot be changed</p>
                </div>

                <div className="col-span-2">
                  <label className="text-xs text-gray-500 font-medium mb-1 block uppercase tracking-wide">
                    {t("profile.gender") || "Gender"}
                  </label>
                  {editMode ? (
                    <div className="flex gap-3">
                      {["Male", "Female", "Other"].map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setFormData({ ...formData, gender: g })}
                          className={`flex-1 py-3 rounded border-2 text-sm font-medium transition-all ${
                            formData.gender === g
                              ? "border-[#18385a] bg-[#18385a] text-white"
                              : "border-gray-200 text-gray-600 hover:border-[#18385a]/40"
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      name="gender"
                      value={formData.gender || ""}
                      readOnly
                      className={inputCls()}
                      placeholder={t("profile.gender") || "Gender"}
                    />
                  )}
                </div>
              </div>

              {editMode && (
                <div className="mt-6 flex gap-4">
                  <button
                    onClick={updateProfile}
                    disabled={saving}
                    className="px-6 py-2 bg-[#18385a] text-white rounded flex items-center gap-2 disabled:opacity-60"
                  >
                    {saving && <Loader2 size={15} className="animate-spin" />}
                    {t("profile.save_changes") || "Save Changes"}
                  </button>
                  <button
                    onClick={() => { setEditMode(false); setFormData(user); }}
                    className="px-6 py-2 border rounded text-gray-600 hover:bg-gray-50"
                  >
                    {t("profile.cancel") || "Cancel"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* MY ORDERS */}
          {activeSection === "orders" && <UserOrders />}

          {/* MANAGE ADDRESSES */}
          {activeSection === "addresses" && (
            <div>
              <h2 className="text-2xl font-semibold font-philosopher mb-6">
                {t("profile.manage_addresses") || "Manage Addresses"}
              </h2>
              <SelectAddressModal />
            </div>
          )}

          {/* WISHLIST */}
          {activeSection === "wishlist" && <WishlistPage />}

          {/* REVIEWS */}
          {activeSection === "reviews" && (
            <p className="text-gray-700">
              {t("profile.no_reviews") || "You have no reviews yet."}
            </p>
          )}

          {/* NOTIFICATIONS */}
          {activeSection === "notifications" && (
            <p className="text-gray-700">
              {t("profile.no_notifications") || "No new notifications."}
            </p>
          )}

          {/* COUPONS */}
          {activeSection === "coupons" && (
            <p className="text-gray-700">
              {t("profile.no_coupons") || "No coupons available."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;